/**
 * Per-tab vs cross-tab state.
 *
 * Super admin impersonation must NOT clobber the super admin's own session
 * in another tab. We use **sessionStorage** for the impersonation token /
 * tenant slug (isolated per tab) and fall back to **localStorage** for the
 * persistent login.
 */
const TOKEN_KEY = "bizil_token";
const TENANT_KEY = "bizil_tenant_slug";

// iter146 — one-time cleanup of a STALE localStorage tenant slug from
// before this fix. Before this change, ``tenantStore.setPersistent()``
// wrote the "sticky" tenant slug to localStorage — global to the
// origin, shared by every open tab. Visiting ``?tenant=<slug>`` in ANY
// tab (a normal dev/preview/testing action — see api.js's
// ``resolveTenantSlug`` docs) silently rewired every OTHER already-open
// tab's next API call to that tenant, even ones showing a completely
// different tenant's branding (user-reported: ApniBachat storefront
// showing Zexo/Witty shops after switching tenants in another tab).
// Same bug class already fixed once for ``/legal/*`` pages (iter68) —
// this generalizes it to the whole app by moving the sticky value to
// sessionStorage (below), which is per-tab by design. Removing the old
// localStorage entry here means an already-affected browser heals on
// its very next load instead of the stale value lingering forever.
if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(TENANT_KEY); } catch { /* ignore */ }
}

export const tokenStore = {
    get() {
        if (typeof window === "undefined") return null;
        return window.sessionStorage.getItem(TOKEN_KEY) || window.localStorage.getItem(TOKEN_KEY);
    },
    set(token, { sessionOnly = false } = {}) {
        if (typeof window === "undefined") return;
        if (sessionOnly) {
            window.sessionStorage.setItem(TOKEN_KEY, token);
        } else {
            window.localStorage.setItem(TOKEN_KEY, token);
        }
    },
    clear() {
        if (typeof window === "undefined") return;
        window.sessionStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(TOKEN_KEY);
    },
    isImpersonation() {
        if (typeof window === "undefined") return false;
        return !!window.sessionStorage.getItem(TOKEN_KEY);
    },
};

export const tenantStore = {
    get() {
        if (typeof window === "undefined") return null;
        // iter146 — sessionStorage ONLY. Previously fell back to
        // localStorage, which is exactly what let one tab's tenant
        // switch bleed into every other open tab (see the cleanup
        // comment above). Nothing writes to localStorage under this
        // key anymore, so there's nothing left to fall back to.
        return window.sessionStorage.getItem(TENANT_KEY);
    },
    // iter146 — despite the name (kept to avoid touching every call
    // site), this now persists PER-TAB via sessionStorage, not
    // cross-tab via localStorage. Still satisfies the original goal —
    // "sticky across SPA navigation" within the same tab — without the
    // cross-tab leak. Real production tenants resolve via subdomain
    // (api.js resolveTenantSlug step 2), which is re-derived fresh on
    // every request regardless of what's in storage, so this is purely
    // the dev/preview ``?tenant=`` fallback path.
    setPersistent(slug) {
        if (typeof window === "undefined") return;
        window.sessionStorage.setItem(TENANT_KEY, slug);
    },
    setSession(slug) {
        if (typeof window === "undefined") return;
        window.sessionStorage.setItem(TENANT_KEY, slug);
    },
    clear() {
        if (typeof window === "undefined") return;
        window.sessionStorage.removeItem(TENANT_KEY);
        window.localStorage.removeItem(TENANT_KEY);
    },
};
